/**
 * Pure helpers for the Reporting module (queued report jobs list). Kept
 * separate from the React components so they can be unit-tested without
 * jsdom/Apollo setup, per AGENTS.md.
 *
 * Queued jobs are presented in two sections. The split is purely cosmetic —
 * both are the same `reportJobs` query against the same endpoints — but it
 * separates the image-led brochure documents ("PDF Generator") from the
 * tabular business reports ("Reporting"), which are used by different people
 * for different jobs.
 */

import { REPORT_CATALOGS } from "../../components/Modals/GenerateReport/utils";

export const REPORT_JOB_STATUSES = {
  draft: "draft",
  pending: "pending",
  queued: "queued",
  processing: "processing",
  cancelRequested: "cancel_requested",
  cancelled: "cancelled",
  complete: "complete",
  failed: "failed",
};

export const REPORT_JOB_STATUS_LABELS = {
  draft: "Draft",
  pending: "Pending",
  queued: "Queued",
  processing: "Processing",
  cancel_requested: "Cancelling",
  cancelled: "Cancelled",
  complete: "Complete",
  failed: "Failed",
};

// Mirrors MAX_ATTEMPTS in api/reports/retryPolicy.js — display only, so the
// count the user sees ("Attempt 2 of 3") matches what the worker will do.
export const MAX_REPORT_JOB_ATTEMPTS = 3;

export const REPORT_JOB_STATUS_STYLES = {
  draft: "bg-amber-100 text-amber-800",
  pending: "bg-indigo-100 text-indigo-700",
  queued: "bg-gray-100 text-gray-700",
  processing: "bg-blue-100 text-blue-700",
  cancel_requested: "bg-orange-100 text-orange-700",
  cancelled: "bg-gray-200 text-gray-700",
  complete: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

/**
 * Pure: is this job still in flight (i.e. worth polling for)? Drafts are
 * deliberately not "active" — they sit still until the user queues them.
 */
export const isReportJobActive = job =>
  job?.status === REPORT_JOB_STATUSES.pending ||
  job?.status === REPORT_JOB_STATUSES.queued ||
  job?.status === REPORT_JOB_STATUSES.processing ||
  job?.status === REPORT_JOB_STATUSES.cancelRequested;

/**
 * Pure: is this job a resumable brochure wizard draft?
 */
export const isResumableDraft = job =>
  job?.status === REPORT_JOB_STATUSES.draft &&
  job?.type === "Instance" &&
  job?.report === "brochure";

/**
 * Pure: can this job be reissued — a finished (or failed) brochure whose
 * saved payload still describes its cohort, so a new draft can be created
 * from it and opened in the wizard with everything preselected.
 */
export const isReissuableReportJob = job => {
  if (job?.type !== "Instance" || job?.report !== "brochure") return false;
  const finished = [
    REPORT_JOB_STATUSES.complete,
    REPORT_JOB_STATUSES.failed,
  ].includes(job?.status);
  if (!finished) return false;
  const payload = job?.payload;
  return (
    (Array.isArray(payload?.instances) && payload.instances.length > 0) ||
    (Array.isArray(payload?.ids) && payload.ids.length > 0)
  );
};

/**
 * Pure: is this job stranded — still claiming to be in flight, but the server
 * has decided the worker abandoned it? `canRetry` is computed server-side (it
 * knows the staleness thresholds) so the list and the mutation can't disagree
 * about which jobs are stuck.
 */
export const isStuckReportJob = job =>
  Boolean(job?.canRetry) && isReportJobActive(job);

/**
 * Pure: can the user put this job back on the queue? Trusts the server's
 * `canRetry`, which covers failed jobs and abandoned ones.
 */
export const isRetryableReportJob = job => Boolean(job?.canRetry);

/**
 * Pure: can this job be deleted? Mirrors the server's
 * `ReportJob.DELETABLE_STATUSES`, plus stuck jobs — without that a job the
 * worker never picked up can't be retried away or removed.
 */
export const isReportJobDeletable = job =>
  [
    REPORT_JOB_STATUSES.draft,
    REPORT_JOB_STATUSES.complete,
    REPORT_JOB_STATUSES.failed,
    REPORT_JOB_STATUSES.cancelled,
  ].includes(job?.status) || isStuckReportJob(job);

/**
 * Pure: "Attempt 2 of 3" once a job has been retried, otherwise "". Only
 * worth showing from the second attempt: the first is just "it ran".
 */
export const formatReportJobAttempts = job =>
  (job?.attempts ?? 0) > 1
    ? `Attempt ${job.attempts} of ${MAX_REPORT_JOB_ATTEMPTS}`
    : "";

/**
 * Pure: the one timestamp worth showing in a list — when the report finished,
 * or when it was asked for if it hasn't. Two columns for the pair meant one of
 * them was always a dash.
 */
export const reportJobDate = job => job?.completedAt || job?.createdAt || null;

/**
 * Pure: that timestamp as a plain date. The exact minute lives in the row's
 * tooltip; the column only has to say roughly when.
 */
export const formatReportJobDate = job => {
  const value = reportJobDate(job);
  return value ? new Date(value).toLocaleDateString() : "—";
};

/**
 * Pure: the full requested / completed times, for the date cell's tooltip, so
 * collapsing the two columns doesn't lose the detail.
 */
export const formatReportJobTimestamps = job =>
  [
    job?.createdAt
      ? `Requested ${new Date(job.createdAt).toLocaleString()}`
      : "",
    job?.completedAt
      ? `Completed ${new Date(job.completedAt).toLocaleString()}`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

/**
 * Pure: does any job in the list warrant continued polling?
 */
export const hasActiveReportJobs = (jobs = []) => jobs.some(isReportJobActive);

/**
 * Pure: "37 of 214" while processing; "" when the totals aren't useful.
 */
export const formatReportJobProgress = job => {
  if (job?.status !== REPORT_JOB_STATUSES.processing) return "";
  if (!job?.progressTotal || job.progressTotal <= 1) return "";
  return `${job.progressCurrent ?? 0} of ${job.progressTotal}`;
};

// slug -> human label across every catalogue, e.g. brochure -> "Instance
// Brochure".
const REPORT_LABELS_BY_SLUG = Object.values(REPORT_CATALOGS).reduce(
  (accumulator, catalog) => ({
    ...accumulator,
    ...Object.fromEntries(
      Object.entries(catalog.reportMeta).map(([slug, meta]) => [
        slug,
        meta.label,
      ]),
    ),
  }),
  {},
);

/**
 * Pure: human label for a job's report flavour, e.g. "Instance Brochure".
 * Falls back to the raw slug for reports the client catalogue doesn't know.
 */
export const reportJobLabel = job =>
  REPORT_LABELS_BY_SLUG[job?.report] || job?.report || "";

/**
 * Pure: which module the report was started in — "Artwork", "Instance",
 * "Exhibition" and so on, which is what the list's Type column says.
 *
 * A brochure is always queued as an Instance report, because instances are what
 * it prints however the works were picked. So the module is read from the
 * indirect cohort the payload remembers: artworks each collapse to an instance,
 * exhibitions expand to the works hung in them. Everything else was started
 * where its entities live.
 *
 * Brochures built before the payload kept its origin read as Instance, which is
 * what the column said for all of them anyway.
 */
export const reportJobSourceType = job => {
  const payload = job?.payload;
  if (payload?.artworkIds?.length > 0) return "Artwork";
  if (payload?.exhibitionIds?.length > 0) return "Exhibition";
  return job?.type || "";
};

/**
 * Singular / plural nouns for a cohort count, e.g. "14 works". Keyed by the
 * job's entity type; an unknown type falls back to "item(s)".
 */
export const REPORT_JOB_ENTITY_NOUNS = {
  Instance: ["work", "works"],
  Artwork: ["artwork", "artworks"],
  Contact: ["contact", "contacts"],
  Invoice: ["invoice", "invoices"],
  Exhibition: ["exhibition", "exhibitions"],
};

/**
 * Pure: "1 work" / "14 works" for a job's entity type.
 */
export const formatReportJobCohortSize = (job, count) => {
  const [one, many] = REPORT_JOB_ENTITY_NOUNS[job?.type] || ["item", "items"];
  return `${count} ${count === 1 ? one : many}`;
};

/**
 * Pure: how many entities the report is of — what the server resolved, or the
 * ids the payload names while it hasn't. Zero for a cohort that is still only a
 * saved list or a search.
 */
export const reportJobCohortCount = job => {
  const totalCount = job?.contents?.totalCount;
  if (totalCount > 0) return totalCount;
  const ids = job?.payload?.ids;
  return Array.isArray(ids) ? ids.length : 0;
};

/**
 * Pure: how much this report covers — one work or many, which is the difference
 * between a single-work document and a whole catalogue, and what the list's Scope
 * column says.
 *
 * The count is the resolved cohort where there is one, and the ids the payload
 * holds where there isn't yet. A cohort saved as a list or a search has no count
 * until the job runs, so it says what it was saved as instead.
 */
export const reportJobScope = job => {
  const count = reportJobCohortCount(job);
  if (count === 1) {
    const [one] = REPORT_JOB_ENTITY_NOUNS[job?.type] || ["item"];
    return `Single ${one}`;
  }
  if (count > 1) return formatReportJobCohortSize(job, count);

  if (job?.payload?.listId != null) return "A saved list";
  if (job?.payload?.search) return "Search results";
  return "";
};

/**
 * Pure: the previewed items of a job's cohort, or [] when the server didn't
 * resolve any (a cohort saved as a list or a search).
 */
export const reportJobContentItems = job => job?.contents?.items || [];

/**
 * Pure: how many entities the preview leaves out, for a "+12 more" suffix.
 * Mirrors `hiddenItemCount` in api/reports/jobContents.js.
 */
export const reportJobHiddenItemCount = job => {
  const items = reportJobContentItems(job);
  const total = job?.contents?.totalCount ?? items.length;
  return Math.max(total - items.length, 0);
};

/**
 * Pure: the items a summary has room to show, and how many entities it stands
 * for without showing — the ones the server left out of the preview plus the
 * ones `limit` dropped.
 *
 * `limit` is for the narrow places, the header's notification dropdown among
 * them: a strip that would wrap onto a second line is cut short and the
 * remainder joins the count instead. No limit shows the whole preview.
 */
export const reportJobContentPreview = (job, limit = null) => {
  const items = reportJobContentItems(job);
  const shown = limit == null ? items : items.slice(0, limit);
  return {
    items: shown,
    hidden: reportJobHiddenItemCount(job) + (items.length - shown.length),
  };
};

/**
 * Pure: what to call this report in a list, derived from the saved payload
 * rather than stored, so the name can never drift from the contents.
 *
 * The chain, most to least specific: the cover title the user typed in the
 * wizard; the first thing in the cohort plus how many others there are; the
 * cohort size; what the cohort was saved as when we didn't resolve it; and
 * finally the report flavour, which is what the list showed before.
 */
export const reportJobName = job => {
  const coverTitle = (job?.payload?.reportCover?.title || "").trim();
  if (coverTitle) return coverTitle;

  const items = reportJobContentItems(job);
  const firstTitle = (items[0]?.title || "").trim();
  if (firstTitle) {
    const hidden = reportJobHiddenItemCount(job);
    const others = Math.max(items.length - 1, 0) + hidden;
    return others > 0 ? `${firstTitle} +${others} more` : firstTitle;
  }

  const totalCount = job?.contents?.totalCount;
  if (totalCount > 0) return formatReportJobCohortSize(job, totalCount);

  if (job?.payload?.listId != null) return "A saved list";
  if (job?.payload?.search) return "Search results";

  return reportJobLabel(job);
};

export const REPORT_SECTIONS = {
  PDF_GENERATOR: "pdfGenerator",
  REPORTING: "reporting",
};

/**
 * The report slugs that belong to the PDF Generator section. Everything else
 * lands in Reporting, so a newly registered report shows up somewhere sensible
 * without a code change here.
 */
export const PDF_GENERATOR_REPORTS = ["brochure"];

export const REPORT_SECTION_META = {
  [REPORT_SECTIONS.PDF_GENERATOR]: {
    label: "PDF Generator",
    path: "/pdf-generator",
    emptyMessage:
      "No brochures yet. Generate one from the PDF Generator action in Artworks, Instances or Exhibitions and it will appear here.",
    // Every report in this section is a brochure, and a brochure only renders
    // as a PDF, so the column would say "PDF" all the way down.
    showsFormatColumn: false,
  },
  [REPORT_SECTIONS.REPORTING]: {
    label: "Reporting",
    path: "/reporting",
    emptyMessage:
      "No reports yet. Generate one from a module's Reporting action and it will appear here.",
    // Reports here come as PDF or XLSX, so which one matters.
    showsFormatColumn: true,
  },
};

/**
 * Pure: presentation metadata for a section, falling back to Reporting so an
 * unknown section still renders.
 */
export const reportSectionMeta = section =>
  REPORT_SECTION_META[section] ||
  REPORT_SECTION_META[REPORT_SECTIONS.REPORTING];

/**
 * Pure: which of the two sections owns a job. Driven by the report slug rather
 * than the entity type, because a brochure can now be built from instances,
 * artworks or exhibitions and should stay in one place regardless.
 */
export const reportJobSection = job =>
  PDF_GENERATOR_REPORTS.includes(job?.report)
    ? REPORT_SECTIONS.PDF_GENERATOR
    : REPORT_SECTIONS.REPORTING;

/**
 * How many rows a section shows at once. The list used to pull 100 and split
 * them here, which meant a page was however many of those 100 happened to
 * belong to the section you were looking at.
 */
export const REPORT_JOB_PAGE_SIZE = 25;

/**
 * Pure: the `reportJobs` filter that narrows the query to one section, so the
 * server pages and counts the same rows the screen shows.
 *
 * PDF Generator asks for its slugs by name; Reporting asks for everything else,
 * which keeps a newly registered report visible somewhere without a change
 * here — the same rule `reportJobSection` follows.
 */
export const reportJobSectionFilter = section =>
  section === REPORT_SECTIONS.PDF_GENERATOR
    ? { reports: PDF_GENERATOR_REPORTS }
    : { excludeReports: PDF_GENERATOR_REPORTS };

/**
 * Pure: how many pages a section's total needs. Always at least one, so an
 * empty section reads "Page 1 of 1" rather than "of 0".
 */
export const reportJobPageCount = count =>
  Math.max(Math.ceil((count ?? 0) / REPORT_JOB_PAGE_SIZE), 1);

/**
 * Pure: "1–25 of 87" above the pager, or "" when there's nothing to describe.
 */
export const formatReportJobRange = ({ count = 0, offset = 0, shown = 0 }) =>
  shown === 0 ? "" : `${offset + 1}–${offset + shown} of ${count}`;

/**
 * Pure: path to whichever section owns this job, with the job highlighted.
 * Used by the header bell so clicking an in-flight report lands on its row in
 * the right section.
 */
export const reportJobHighlightPath = job => {
  const { path } = reportSectionMeta(reportJobSection(job));
  return job?.id ? `${path}?highlight=${job.id}` : path;
};

export const HIGHLIGHT_QUERY_PARAM = "highlight";

/**
 * Pure: the highlighted job id from a location search string, or null.
 */
export const highlightedJobIdFromSearch = search => {
  const raw = new URLSearchParams(search || "").get(HIGHLIGHT_QUERY_PARAM);
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

// The page a list is on lives in the url, so it survives a reload and can be
// linked to. It reads as the page the pager shows — one-based.
export const PAGE_QUERY_PARAM = "page";

/**
 * Pure: which page a location search string asks for, zero-based for the query's
 * offset. A missing or nonsense page reads as the first.
 */
export const reportJobPageFromSearch = search => {
  const raw = new URLSearchParams(search || "").get(PAGE_QUERY_PARAM);
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) || parsed < 1 ? 0 : parsed - 1;
};

/**
 * Pure: the search string for a zero-based page, keeping whatever else the
 * current one carries (the highlighted job, say). The first page drops the
 * parameter rather than spelling out the default.
 */
export const reportJobPageSearch = (search, page) => {
  const params = new URLSearchParams(search || "");
  if (page > 0) params.set(PAGE_QUERY_PARAM, String(page + 1));
  else params.delete(PAGE_QUERY_PARAM);
  const query = params.toString();
  return query ? `?${query}` : "";
};
